import { useEffect, useRef, useState } from "react";
import { DEFAULT_USD_RATE, DEFAULT_EUR_RATE, type SidebarSettings } from "../types/ui";
import type { Book } from "../types/api";
import InfoTip from "./InfoTip";

interface Props {
  settings: SidebarSettings;
  onChange: (s: SidebarSettings) => void;
  collapsed: boolean;
  onToggle: () => void;
  // Which book this run belongs to. AR has no commission track at all (commission is CRI
  // revenue, never a customer receivable), so its commission tolerance controls nothing -
  // a knob that appears to affect the figures and cannot. Absent = AP, as everywhere.
  book?: Book;
  // Month-end of the reconciliation month picked in the Run dialog, as "YYYY-MM-DD"
  // ("" = Automatic). Shown in the To box so the cut-off finance chose is visible where
  // they expect it. DISPLAY ONLY: it is deliberately NOT written into settings.periodTo,
  // because that field is sent as the engine's period_to and would engage the
  // transaction-window filter — a different parameter from the month's period_override,
  // and a change to the reconciled figures. Editing the box still applies normally.
  reconMonthEnd?: string;
  // Rates loaded from the user's SAVED set rather than the standing default. A rate
  // persisted from an earlier session silently multiplies every foreign-currency
  // figure, so the boxes carrying one are called out rather than left to look like
  // the default — finance must see it BEFORE the run, not after the numbers look odd.
  savedRates?: { usd?: number; eur?: number };
  // The rate signature the user has already acknowledged this session, and the setter
  // that records a new one. Dismissal is per VALUE, so a later change to the saved rate
  // re-raises the warning rather than staying hidden behind a stale acknowledgement.
  rateNoticeAck?: string;
  onAckRateNotice?: (sig: string) => void;
  // Why the re-run controls are read-only, or absent when they are live. Set on a
  // dashboard restored after a refresh: it holds no uploaded files to re-run against, so
  // an edit could only change the box while every figure stayed on the old settings.
  locked?: string;
}

// A number field that commits on BLUR or ENTER instead of on every keystroke.
//
// Why: the dashboard re-runs the WHOLE reconciliation when one of these changes
// (~10s on Dubai). With a per-character onChange, typing "1000" fired runs for 1, 10
// and 100 — the keystroke pause, not the user's intent, decided what got reconciled.
// Committing on blur/Enter means exactly one run, for the value actually chosen.
// Escape reverts to the last committed value.
function CommittedNumber({ value, onCommit, step, min, id, disabled }: {
  value: number; onCommit: (v: number) => void; step: string; min?: string; id?: string;
  disabled?: boolean;
}) {
  const [draft, setDraft] = useState(String(value));
  // Track external changes (per-insurer period defaults, a restored session) unless
  // the user is mid-edit — clobbering their typing would be worse than a stale draft.
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(String(value)); }, [value, editing]);
  // Escape blurs, and the blur handler still closes over the TYPED draft (the revert's
  // setDraft has not rendered yet), so without this flag Escape committed the value it
  // was meant to discard.
  const reverting = useRef(false);

  function commit() {
    setEditing(false);
    if (reverting.current) { reverting.current = false; setDraft(String(value)); return; }
    const n = Number(draft);
    if (draft.trim() === "" || Number.isNaN(n)) { setDraft(String(value)); return; }
    if (n !== value) onCommit(n);
  }

  return (
    <input
      id={id} className="input" type="number" step={step} min={min} value={draft}
      disabled={disabled}
      onFocus={() => setEditing(true)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.currentTarget.blur(); }
        else if (e.key === "Escape") { reverting.current = true; e.currentTarget.blur(); }
      }}
    />
  );
}

// The period window is edited as a DRAFT and applied by an explicit button, so nothing
// re-runs until the user says so. A date input emits a change per SEGMENT and a
// complete, well-formed date the instant the last segment fills, so no event here can
// separate "chose this date" from "passed through it while typing" — committing on any
// of them fired a reconciliation per segment. Blur can't carry the commit either: both
// bounds share one Apply, and clicking it would blur the field first.
//
// Draft state lives in Sidebar (not here) because ONE button spans BOTH bounds — that
// is also what lets From and To move together in a single run, instead of reconciling
// the intermediate (new From, old To) window nobody asked for.
function DraftDate({ value, onChange, onApply, onRevert, id, disabled }: {
  value: string; onChange: (v: string) => void; onApply: () => void; onRevert: () => void; id?: string;
  disabled?: boolean;
}) {
  return (
    <input
      id={id} className="input" type="date" value={value} disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { e.preventDefault(); onApply(); }
        else if (e.key === "Escape") { onRevert(); e.currentTarget.blur(); }
      }}
    />
  );
}

// Sidebar settings — mirrors app/upload.py::_render_sidebar: reconciled tolerance,
// per-track Matched tolerances (premium/commission), as-of date, period From/To.
// Collapsible like Streamlit's built-in sidebar toggle.
export default function Sidebar({ settings, onChange, collapsed, onToggle, reconMonthEnd, savedRates, rateNoticeAck, onAckRateNotice, book, locked }: Props) {
  // Display only: the stored tolerance is left untouched, so switching books back shows
  // the value the user last set rather than a silently reset one.
  const showCommission = book !== "AR";
  const set = (patch: Partial<SidebarSettings>) => onChange({ ...settings, ...patch });

  // Applied period window vs the one being edited. Kept in step when the window moves
  // from outside (per-insurer defaults, a restored session) — but only while the draft
  // is clean, so an external update can't silently discard a half-finished edit.
  const appliedFrom = settings.periodFrom ?? "";
  const appliedTo = settings.periodTo ?? "";
  const [draft, setDraft] = useState({ from: appliedFrom, to: appliedTo });
  // The Run dialog's month-end is shown in the To box but deliberately NOT written into
  // settings.periodTo (see reconMonthEnd below), so the box can display a date while the
  // applied value is still "". Clearing it then returned draft.to to "" == appliedTo,
  // `dirty` went false and the Apply button UNMOUNTED mid-gesture — the user could see a
  // date, delete it, and have no way to apply the clear. Tracking what the box was
  // seeded with lets "cleared a seeded date" count as an edit, which is what it is.
  const [seededTo, setSeededTo] = useState("");
  const clearedSeed = seededTo !== "" && draft.to === "" && appliedTo === "";
  const dirty = draft.from !== appliedFrom || draft.to !== appliedTo || clearedSeed;
  // "Clean" must be judged against the PREVIOUS applied window, not the current one.
  // On the render that delivers an external change the two already differ, so `dirty`
  // is true for a draft the user never touched — the guard then discarded every
  // per-insurer default and the box stayed blank while the engine floored the run
  // (Dubai's 2025-01-01 and every CRI-derived From never reached the UI). Comparing
  // to the last applied value distinguishes "the window moved beneath us" (adopt it)
  // from "the user is mid-edit" (keep their draft).
  const lastApplied = useRef({ from: appliedFrom, to: appliedTo });
  useEffect(() => {
    const untouched = draft.from === lastApplied.current.from
                   && draft.to === lastApplied.current.to;
    lastApplied.current = { from: appliedFrom, to: appliedTo };
    if (untouched) setDraft({ from: appliedFrom, to: appliedTo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFrom, appliedTo]);

  // Mirror the Run dialog's chosen month-end into the To box. Keyed on the value so it
  // seeds once per pick rather than on every render, which would fight the user's typing.
  // Only the DRAFT is set — settings.periodTo is untouched, so nothing new is sent to the
  // engine and the run stays byte-identical; the box is then an ordinary editable field
  // the user can overwrite or clear, and Apply period works exactly as before.
  const lastReconMonthEnd = useRef("");
  useEffect(() => {
    if (!reconMonthEnd || reconMonthEnd === lastReconMonthEnd.current) return;
    lastReconMonthEnd.current = reconMonthEnd;
    setDraft((d) => ({ ...d, to: reconMonthEnd }));
    setSeededTo(reconMonthEnd);
  }, [reconMonthEnd]);

  // A bound is applicable when it is blank (deliberately cleared -> unbounded on that
  // side) or a complete date. <input type="date"> only ever reports a FULL YYYY-MM-DD
  // or "" — a partially typed date is reported as "" but so is a real clear, and the
  // two are indistinguishable here, so blank must be treated as the clear it usually is.
  // Requiring `applied === ""` instead made clearing impossible: combined with `dirty`
  // (draft must differ from applied), it could only ever pass when the field was
  // ALREADY blank, i.e. when there was nothing to clear.
  // Rates that are BOTH saved and non-standard — the only ones worth warning about. A
  // saved rate the user has since overtyped is not carried over (the box no longer
  // holds it), and one equal to the standing default changes nothing.
  const carriedRates: string[] = [];
  if (savedRates?.usd !== undefined && savedRates.usd === settings.usdRate
      && settings.usdRate !== DEFAULT_USD_RATE) carriedRates.push(`USD ${settings.usdRate}`);
  if (savedRates?.eur !== undefined && savedRates.eur === settings.eurRate
      && settings.eurRate !== DEFAULT_EUR_RATE) carriedRates.push(`EUR ${settings.eurRate}`);
  // Identity of exactly what is being warned about, so a dismissal covers THIS pair of
  // rates and nothing else. Changing either rate yields a new signature and re-raises.
  const carriedSig = carriedRates.join("|");

  const applicable = (d: string) => d === "" || /^\d{4}-\d{2}-\d{2}$/.test(d);
  const inverted = Boolean(draft.from && draft.to && draft.from > draft.to);
  // The To box is not the only upper bound: with it EMPTY the recon month's cut-off
  // still bounds the window (the engine's period_override -> _eff_period_to). A From
  // past that cut-off leaves nothing in the window at all — the balances come back as
  // at the cut-off — and `inverted` cannot see it, since it compares the two boxes.
  // Blocked like `inverted`: the empty window reads RECONCILED on nothing.
  const pastCutoff = Boolean(draft.from && !draft.to && reconMonthEnd
                             && draft.from > reconMonthEnd);
  const canApply = dirty && applicable(draft.from) && applicable(draft.to) && !inverted
                   && !pastCutoff;

  function applyPeriod() {
    if (!canApply) return;
    // Emptying a From that HAD a value is the explicit "show me everything" gesture,
    // and the engine needs to be told: otherwise its cri_period_floor default silently
    // re-imposes the CRI ledger's start and pre-floor rows stay out of the window.
    // Latched (not recomputed from the blank value) so the intent survives the re-run;
    // setting any From again clears it.
    const cleared = draft.from === ""
      ? (appliedFrom !== "" || Boolean(settings.periodFromCleared))
      : false;
    // Emptying a To that the Run dialog SEEDED is the "no ceiling" gesture. periodTo is
    // already "" in that state (the seed is display-only), so this flag is the only thing
    // that distinguishes it from an untouched load — without it the re-run key never
    // moves and Apply period silently does nothing. It also retires the recon_month
    // override downstream, which is what re-imposed the cutoff.
    const toCleared = draft.to === ""
      ? (clearedSeed || appliedTo !== "" || Boolean(settings.periodToCleared))
      : false;
    // The seed has been superseded by an explicit apply, so it must stop counting as an
    // edit — otherwise `dirty` would stay true forever and the button would never retire.
    setSeededTo("");
    set({ periodFrom: draft.from, periodTo: draft.to,
          periodFromCleared: cleared, periodToCleared: toCleared });
  }

  if (collapsed) {
    return (
      <aside className="sidebar collapsed">
        <button className="sidebar-toggle" onClick={onToggle} title="Show settings" aria-label="Show settings">›</button>
      </aside>
    );
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-head">
        <h3 style={{ margin: 0 }}>Settings</h3>
        <button className="sidebar-toggle" onClick={onToggle} title="Hide settings" aria-label="Hide settings">‹</button>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        Reconciliation controls. The Matched tolerances and rate re-run the
        reconciliation — every figure updates — and apply when you leave the field or
        press Enter; the dates apply on the button below them.
      </p>
      {locked && (
        <div className="badge badge-warn" role="note" style={{ display: "block", margin: "0 0 8px" }}>
          {locked}
        </div>
      )}

      <label className="field-label" htmlFor="sb-tol-premium">
        Matched tolerance — Premium (AED)
        <InfoTip text="How far two amounts can differ and still count as Matched on the Premium track. 0.00 = amounts must match to the fils. Loosen slightly to absorb rounding." />
      </label>
      <CommittedNumber id="sb-tol-premium" step="0.01" disabled={!!locked} value={settings.tolPremium}
        onCommit={(v) => set({ tolPremium: v })} />

      {showCommission && (
        <>
          <label className="field-label" htmlFor="sb-tol-commission">
            Matched tolerance — Commission (AED)
            <InfoTip text="Same as the Premium tolerance, but for the Commission track — the two tracks match independently." />
          </label>
          <CommittedNumber id="sb-tol-commission" step="0.01" disabled={!!locked} value={settings.tolCommission}
            onCommit={(v) => set({ tolCommission: v })} />
        </>
      )}

      {/* Carried-over rate notice. Shown only when a SAVED rate is actually in force
          AND differs from the standing default — a saved rate equal to the default
          changes nothing, and warning about it would train finance to ignore the
          banner. Sits ABOVE the rate boxes so it is read before they are edited.
          Dismissable, but the acknowledgement is keyed on the rate VALUE and lasts
          only this session (see Props.rateNoticeAck), so it can never silence a
          different rate or carry into the next closing period. */}
      {carriedRates.length > 0 && rateNoticeAck !== carriedSig && (
        <div className="badge badge-warn" style={{ display: "block", margin: "12px 0 0" }}>
          <strong>Using your saved rate{carriedRates.length > 1 ? "s" : ""}:</strong>{" "}
          {carriedRates.join(" · ")} — carried over from a previous session, not the
          standard rate. Check this is the rate for the period you are closing.
          <button
            className="btn"
            style={{ display: "block", marginTop: 8, padding: "2px 10px", fontSize: "0.75rem" }}
            onClick={() => onAckRateNotice?.(carriedSig)}
            title="Hide this warning for this rate until you sign in again"
          >
            Got it
          </button>
        </div>
      )}

      <label className="field-label" htmlFor="sb-usd-rate">
        USD → AED conversion rate
        <InfoTip text="The rate used to convert USD figures on an insurer's statement into AED before anything is matched or totalled. Defaults to 3.6725. Only affects insurers whose statements actually carry USD amounts — everyone else is unchanged." />
      </label>
      <CommittedNumber id="sb-usd-rate" step="0.0001" min="0" disabled={!!locked} value={settings.usdRate}
        onCommit={(v) => set({ usdRate: v })} />
      {!(settings.usdRate > 0) && (
        <p className="muted" style={{ color: "var(--risk)", marginTop: 4 }}>
          Rate must be greater than zero — reverting to {DEFAULT_USD_RATE} for this run.
        </p>
      )}

      <label className="field-label" htmlFor="sb-eur-rate">
        EUR → AED conversion rate
        <InfoTip text="The rate used to convert EUR figures on an insurer's statement into AED before anything is matched or totalled. Defaults to 4.2358. Unlike USD, the AED is not pegged to the EUR, so check this against the rate finance used for the period. Only affects insurers whose statements actually carry EUR amounts — everyone else is unchanged." />
      </label>
      <CommittedNumber id="sb-eur-rate" step="0.0001" min="0" disabled={!!locked} value={settings.eurRate}
        onCommit={(v) => set({ eurRate: v })} />
      {!(settings.eurRate > 0) && (
        <p className="muted" style={{ color: "var(--risk)", marginTop: 4 }}>
          Rate must be greater than zero — reverting to {DEFAULT_EUR_RATE} for this run.
        </p>
      )}

      <label className="field-label" htmlFor="sb-period-from">
        Transaction Period — From
        <InfoTip text="Only reconcile transactions dated in this window. Opening balances still carry in. Leave blank for the full history." />
      </label>
      <DraftDate id="sb-period-from" disabled={!!locked} value={draft.from}
        onChange={(v) => setDraft((d) => ({ ...d, from: v }))}
        onApply={applyPeriod} onRevert={() => setDraft({ from: appliedFrom, to: appliedTo })} />

      <label className="field-label" htmlFor="sb-period-to">
        Transaction Period — To
        <InfoTip text="End of the transaction window — the reconciliation cut-off. Transactions after it are excluded and the report is headed with this date. Leave blank to include everything after the From date." />
      </label>
      <DraftDate id="sb-period-to" disabled={!!locked} value={draft.to}
        onChange={(v) => setDraft((d) => ({ ...d, to: v }))}
        onApply={applyPeriod} onRevert={() => setDraft({ from: appliedFrom, to: appliedTo })} />

      {/* Shown only once the window actually differs from the applied one — an edit is
          the only thing that makes applying meaningful, and reverting hides it again.
          Disabled (not hidden) on a half-typed or inverted window so the reason stays
          on screen instead of the button vanishing under the cursor. */}
      {dirty && (
        <div>
          <button className="btn btn-primary btn-apply"
            onClick={applyPeriod} disabled={!canApply}
            title="Re-runs the reconciliation over the new period">
            Apply period
          </button>
          {inverted && (
            <p className="muted" style={{ color: "var(--risk)", marginTop: 4 }}>
              From ({draft.from}) is after To ({draft.to}).
            </p>
          )}
          {pastCutoff && (
            <p className="muted" style={{ color: "var(--risk)", marginTop: 4 }}>
              From ({draft.from}) is after the {reconMonthEnd} cut-off — no
              transactions fall in the period.
            </p>
          )}
        </div>
      )}
    </aside>
  );
}
